import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token from localStorage as fallback (cross-domain cookies can fail)
api.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem("reason_token");
  if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  const tid = localStorage.getItem("reason_tenant_override");
  if (tid) cfg.headers["X-Tenant-Id"] = tid;
  return cfg;
});

// RBAC safety net: if any page-load read (GET) is denied (403), the user reached something they
// can't view — send them to their landing page instead of surfacing a permission-denied toast.
// (Nav + palette already hide unauthorized items; this covers direct-URL / nested-route edge cases
// so sidebar visibility and effective access stay consistent.)
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const method = (err?.config?.method || "get").toLowerCase();
    const url = err?.config?.url || "";
    // RBAC safety-net redirect fires ONLY for primary page reads. Background/optional fetches (e.g. the
    // New-User modal's scope dropdowns) opt out via `suppressAuthRedirect` so an incidental 403 on a
    // supplementary endpoint can't hijack the whole page to the landing route. Route guards + backend
    // RBAC still enforce access; those queries already degrade gracefully to an empty option list.
    if (status === 403 && method === "get" && !err?.config?.suppressAuthRedirect) {
      err.__rbacRedirect = true;
      // Resolve the caller's role landing page (/me/home) rather than a generic /app bounce, so a
      // blocked deep-link lands them somewhere they can actually use. Guard against a redirect loop.
      if (typeof window !== "undefined" && !url.includes("/me/home")) {
        let dest = "/app";
        try { dest = (await api.get("/me/home")).data?.route || "/app"; } catch { /* fallback */ }
        if (window.location.pathname !== dest) window.location.assign(dest);
      }
    }
    // E1C R2 — session invalidated (logout / takeover from another device / admin terminate / disabled /
    // legacy no-sid token). Any protected call now 401s: clear the stale token and bounce to /login with
    // a "session expired" notice. Skip the auth/bootstrap endpoints (they 401 by design for guests).
    if (status === 401) {
      const authFlow = ["/auth/login", "/auth/mfa/verify-login", "/auth/register", "/auth/refresh",
        "/auth/me", "/auth/logout", "/auth/login/reset-session", "/security/me/permissions"]
        .some((p) => url.includes(p));
      const hadToken = typeof window !== "undefined" && !!localStorage.getItem("reason_token");
      if (!authFlow && hadToken && typeof window !== "undefined") {
        try { localStorage.removeItem("reason_token"); } catch { /* noop */ }
        try { sessionStorage.setItem("reason_session_expired", "1"); } catch { /* noop */ }
        if (!window.location.pathname.startsWith("/login")) {
          window.dispatchEvent(new Event("reason-auth-changed"));
          window.location.assign("/login");
        }
      }
    }
    return Promise.reject(err);
  }
);

// Absolute URL for media served by the backend (local storage returns relative /api paths)
export const mediaUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND_URL}${u}`);


export function formatApiError(err) {
  const status = err?.response?.status;
  const d = err?.response?.data?.detail ?? err?.response?.data;
  const raw = typeof d === "string" ? d : d?.msg;
  // RBAC denials are handled by redirect (above) — never show a permission-denied toast.
  if (err?.__rbacRedirect) return "";
  if (status === 403 || (typeof raw === "string" && raw.toLowerCase().startsWith("missing permission"))) {
    // Non-GET (button/action) 403: a subtle one-line hint instead of an empty flash.
    return "This action isn't available for your role.";
  }
  if (d == null) return err?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  if (d?.message) return d.message;
  return JSON.stringify(d);
}
